import { Alert, Button, Form, Select, Space, Switch } from 'antd'
import { useEffect, useState } from 'react'
import { usePageOutletContext } from '../../page-outlet-context'

type ScannerConfigurationFormInput = {
  serialPort: string
  baudRate: number
  enabled: boolean
}

// Default values for the scanner Zebra DS9308
const DEFAULT_BAUD_RATE = 9600
const BAUD_RATE_OPTIONS = [9600, 19200, 38400, 57600, 115200]

export const ScannerConfiguration: React.FC = () => {
  const { notificationInstance } = usePageOutletContext()
  const [form] = Form.useForm<ScannerConfigurationFormInput>()
  const [availablePorts, setAvailablePorts] = useState<Array<{ value: string; label: string }>>([])

  useEffect(() => {
    const savedSerialPortInfo = localStorage.getItem('scanner_serial_port_info') || ''
    const savedBaudRate = parseInt(
      localStorage.getItem('scanner_baud_rate') || String(DEFAULT_BAUD_RATE)
    )
    const savedEnabled = localStorage.getItem('scanner_enabled') === 'true'

    form.setFieldsValue({
      serialPort: savedSerialPortInfo,
      baudRate: savedBaudRate,
      enabled: savedEnabled
    })

    if (navigator.serial) {
      listAvailablePorts()
    }
  }, [form])

  // Function to list available serial ports
  const listAvailablePorts = async () => {
    try {
      // Request access to serial ports
      const ports = await navigator.serial.getPorts()

      // If no authorized ports are found, show an informative message
      if (ports.length === 0) {
        notificationInstance.info({
          message: 'No hay puertos seriales autorizados',
          description:
            'Haz clic en "Buscar puertos" para autorizar un puerto serial',
          duration: 5
        })
      } else {
        // Try to get more information about the ports
        const portInfos = ports.map((port, index) => {
          const info = port.getInfo()
          const portIdentifier = JSON.stringify({
            index,
            usbVendorId: info.usbVendorId ?? null,
            usbProductId: info.usbProductId ?? null
          })
          return {
            value: portIdentifier,
            label: `Puerto ${index + 1} (V:${info.usbVendorId ?? 'N/A'}, P:${info.usbProductId ?? 'N/A'})`
          }
        })

        setAvailablePorts(portInfos)

        // Load the saved port if it exists in the list
        const savedSerialPortInfo = localStorage.getItem('scanner_serial_port_info')
        if (
          savedSerialPortInfo &&
          portInfos.some(p => p.value === savedSerialPortInfo)
        ) {
          form.setFieldsValue({ serialPort: savedSerialPortInfo })
        }

        notificationInstance.success({
          message: `${ports.length} puerto(s) disponible(s)`,
          description:
            'Se han encontrado puertos seriales. Selecciona el puerto del escáner.',
          duration: 3
        })
      }
    } catch (error) {
      notificationInstance.error({
        message: 'Error al listar puertos',
        description: 'No se pudieron listar los puertos seriales disponibles',
        duration: 3
      })
    }
  }

  // Function to request access to a new serial port
  const requestPortAccess = async () => {
    try {
      if (navigator.serial) {
        // Request user to select a port
        await navigator.serial.requestPort()
        // Update the list of available ports
        await listAvailablePorts()

        notificationInstance.success({
          message: 'Puerto autorizado',
          description: 'Se ha autorizado un nuevo puerto serial',
          duration: 3
        })
      } else {
        throw new Error('Web Serial API no soportada')
      }
    } catch (error) {
      if ((error as Error).message !== 'No port selected by the user.') {
        notificationInstance.error({
          message: 'Error de acceso',
          description: 'No se pudo acceder al puerto serial. Asegúrate de que tu navegador soporte Web Serial API.',
          duration: 3
        })
      }
    }
  }

  const showSuccess = () =>
    notificationInstance.success({
      message: 'Configuración guardada',
      description: 'Se ha actualizado con éxito la configuración del escáner',
      duration: 3
    })

  const onSaveConfiguration = async (values: ScannerConfigurationFormInput) => {
    localStorage.setItem('scanner_serial_port_info', values.serialPort)
    localStorage.setItem('scanner_baud_rate', String(values.baudRate))
    localStorage.setItem('scanner_enabled', String(values.enabled))

    showSuccess()
  }

  return (
    <>
      <h3>Configuración del escáner de códigos de barras en modo serial (CDC/RS-232)</h3>

      <Alert
        type="info"
        showIcon
        className="mb-5"
        message="Instrucciones para configurar el escáner en modo CDC (Serial)"
        description={
          <>
            <p><strong>1.</strong> Asegúrate de que el escáner esté conectado al ordenador mediante USB.</p>
            <p><strong>2.</strong> Configura el escáner en modo CDC (Serial) escaneando los siguientes códigos en el manual:</p>
            <ul>
              <li>Escanea el código "USB CDC" en el manual del escáner (Pág. 240 Zebra DS9308)</li>
              <li>Escanea el código "Enter Key" (Pág. 88 Zebra DS9308)</li>
            </ul>
            <p><strong>3.</strong> Haz clic en "Buscar puertos" y selecciona el puerto del escáner.</p>
            <p><strong>4.</strong> Guarda la configuración.</p>
          </>
        }
      />
      <Form
        form={form}
        onFinish={onSaveConfiguration}
        layout="vertical"
      >
        <Form.Item name="enabled" label="Activar escáner" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Space>
          <Form.Item name="serialPort" label="Puerto serial">
            <Select
              placeholder="Selecciona un puerto"
              className="w-full"
              options={availablePorts}
            />
          </Form.Item>
          <Form.Item>
            <Button onClick={requestPortAccess} style={{ marginTop: 30 }}>
              Buscar puertos
            </Button>
          </Form.Item>
        </Space>

        <Form.Item name="baudRate" label="Velocidad (baudios)">
          <Select
            options={BAUD_RATE_OPTIONS.map(rate => ({ value: rate, label: String(rate) }))}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">Guardar</Button>
        </Form.Item>
      </Form>
    </>
  )
}
